import React from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { UserMenu } from '../components/Layout/UserMenu';
import { MembersManager } from '../components/Settings/MembersManager';
import { ComponentDemo } from './admin/ComponentDemo';
import { OrgsList } from './admin/OrgsList';
import { UsersList } from './admin/UsersList';
import { OrgDetails } from './admin/OrgDetails';
import { AdminDashboard } from './admin/AdminDashboard';
import { InvitesList } from './admin/InvitesList';
import { LLMUsage } from './admin/LLMUsage';
import { LLMLogs } from './admin/LLMLogs';
import { JobsDashboard } from './admin/JobsDashboard';
import { JobsList } from './admin/JobsList';
import { ScheduledJobs } from './admin/ScheduledJobs';
import { AirportsManagement } from './admin/AirportsManagement';
import { AdminSettings } from './admin/AdminSettings';
import { SidebarLayout } from '../components/Layout/SidebarLayout';
import { FullscreenLayout } from '../components/Layout/FullscreenLayout';
import { OrgOverview } from './organization/OrgOverview';
import { MembersAndInvites } from './organization/MembersAndInvites';
import { RolesAndPermissions } from './organization/RolesAndPermissions';
import { OrgAttachments } from './organization/OrgAttachments';
import { BrandGuidelines } from './organization/BrandGuidelines';
import { AboutCompany } from './organization/AboutCompany';
import { CompanyKnowledge } from './organization/CompanyKnowledge';
import { AISettings } from './organization/AISettings';
import { BillingSettings } from './organization/BillingSettings';
import { GeneralSettings } from './organization/GeneralSettings';
import { Clients } from './organization/Clients';
import { ClientDashboard } from './organization/ClientDashboard';
import { ClientEvents } from './organization/ClientEvents';
import { EventDashboard } from './organization/EventDashboard';
import { EventAttendees } from './organization/EventAttendees';
// Canvas and Content Plan tools removed in Milestone 7
// BackgroundGeneratorPage removed in Milestone 3
import { DashboardPage } from './dashboard/DashboardPage';
import { Onboarding } from './Onboarding';
import { FlightCostEstimator } from './tools/flight-cost/FlightCostEstimator';
import { FlightCostAssessment } from './tools/flight-cost/FlightCostAssessment';
import { FlightManifests } from './tools/flight-manifests/FlightManifests';
import { EventManifests } from './tools/flight-manifests/EventManifests';
import { LeadEnrichmentComingSoon } from './tools/lead-enrichment/ComingSoon';
import { ProfilePage } from './settings/Account/ProfilePage';
import { SecurityPage } from './settings/Account/SecurityPage';
import { SessionsPage } from './settings/Account/SessionsPage';
import { DangerPage } from './settings/Account/DangerPage';
import { SeaVentSearch } from './tools/seavent/SeaVentSearch';
import { ResearchProposalTool } from './tools/research-proposal/ResearchProposalTool';
import { VenuesManagement } from './venues/VenuesManagement';
import { VenueResearch } from './venues/VenueResearch';
import { Documentation } from './organization/Documentation';
import { DestinationLibrary } from './organization/DestinationLibrary';
import { EventLibrary } from './organization/EventLibrary';
import { ActivityLibrary } from './organization/ActivityLibrary';
import { Forbidden } from './Forbidden';
import { PermissionProtectedRoute } from '@/components/routing/PermissionProtectedRoute';
import { PERMISSIONS } from '@/constants/permissions';

export function AppRouter() {
  return (
    <>
      <Routes>
        <Route path="/dashboard" element={<DashboardWithHeader />} />
        {/* Assets, templates, canvas routes removed in post-refactor cleanup */}
        <Route path="/tools/flight-cost" element={<FullscreenLayout><FlightCostEstimator /></FullscreenLayout>} />
        <Route path="/tools/flight-cost/:id" element={<FullscreenLayout><FlightCostAssessment /></FullscreenLayout>} />
        <Route path="/tools/flight-manifests" element={<FullscreenLayout><FlightManifests /></FullscreenLayout>} />
        <Route path="/tools/flight-manifests/:eventId" element={<FullscreenLayout><EventManifests /></FullscreenLayout>} />
        <Route path="/tools/lead-enrichment" element={<FullscreenLayout><LeadEnrichmentComingSoon /></FullscreenLayout>} />
        <Route path="/tools/research-proposal/:eventId" element={<ResearchProposalTool />} />
        <Route path="/tools/research-proposal" element={<ResearchProposalTool />} />
        <Route path="/attachments" element={<DashboardWithHeader />} />
        <Route path="/tools/seavent" element={<FullscreenLayout><SeaVentSearch /></FullscreenLayout>} />
        <Route path="/admin" element={<SidebarLayout type="admin"><AdminDashboard /></SidebarLayout>} />
        <Route path="/admin/orgs" element={<SidebarLayout type="admin"><OrgsList /></SidebarLayout>} />
        <Route path="/admin/orgs/:id" element={<SidebarLayout type="admin"><OrgDetails /></SidebarLayout>} />
        <Route path="/admin/users" element={<SidebarLayout type="admin"><UsersList /></SidebarLayout>} />
        <Route path="/admin/invites" element={<SidebarLayout type="admin"><InvitesList /></SidebarLayout>} />
        <Route path="/admin/styleguide" element={<SidebarLayout type="admin"><ComponentDemo /></SidebarLayout>} />
        <Route path="/admin/llm/usage" element={<SidebarLayout type="admin"><LLMUsage /></SidebarLayout>} />
        <Route path="/admin/llm/logs" element={<SidebarLayout type="admin"><LLMLogs /></SidebarLayout>} />
        <Route path="/admin/jobs" element={<SidebarLayout type="admin"><JobsDashboard /></SidebarLayout>} />
        <Route path="/admin/jobs/list" element={<SidebarLayout type="admin"><JobsList /></SidebarLayout>} />
        <Route path="/admin/scheduled-jobs" element={<SidebarLayout type="admin"><ScheduledJobs /></SidebarLayout>} />
        <Route path="/admin/airports" element={<SidebarLayout type="admin"><AirportsManagement /></SidebarLayout>} />
        <Route path="/admin/settings" element={<SidebarLayout type="admin"><AdminSettings /></SidebarLayout>} />
        <Route path="/organization" element={<SidebarLayout type="organization"><OrgOverview /></SidebarLayout>} />
        <Route path="/organization/members" element={
          <SidebarLayout type="organization">
            <PermissionProtectedRoute 
              anyPermissions={[PERMISSIONS.VIEW_USERS, PERMISSIONS.MANAGE_USERS]}
              deniedMessage="You need permission to view members and invites"
            >
              <MembersAndInvites />
            </PermissionProtectedRoute>
          </SidebarLayout>
        } />
        <Route path="/organization/settings/roles" element={
          <SidebarLayout type="organization">
            <PermissionProtectedRoute 
              permission={PERMISSIONS.MANAGE_ROLES}
              deniedMessage="You need permission to manage roles and permissions"
            >
              <RolesAndPermissions />
            </PermissionProtectedRoute>
          </SidebarLayout>
        } />
        <Route path="/forbidden" element={<SidebarLayout type="organization"><Forbidden /></SidebarLayout>} />
        <Route path="/organization/attachments" element={
          <SidebarLayout type="organization">
            <PermissionProtectedRoute 
              permission={PERMISSIONS.MANAGE_ATTACHMENTS}
              deniedMessage="You need permission to manage attachments"
            >
              <OrgAttachments />
            </PermissionProtectedRoute>
          </SidebarLayout>
        } />
        <Route path="/organization/knowledge" element={
          <SidebarLayout type="organization">
            <PermissionProtectedRoute 
              permission={PERMISSIONS.MANAGE_KNOWLEDGE}
              deniedMessage="You need permission to manage company knowledge"
            >
              <CompanyKnowledge />
            </PermissionProtectedRoute>
          </SidebarLayout>
        } />
        {/* Organization settings routes - require MANAGE_ORGANIZATION_SETTINGS */}
        <Route path="/organization/brand-guidelines" element={
          <SidebarLayout type="organization">
            <PermissionProtectedRoute 
              permission={PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS}
              deniedMessage="You need permission to manage organization settings"
            >
              <BrandGuidelines />
            </PermissionProtectedRoute>
          </SidebarLayout>
        } />
        <Route path="/organization/about" element={
          <SidebarLayout type="organization">
            <PermissionProtectedRoute 
              permission={PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS}
              deniedMessage="You need permission to manage organization settings"
            >
              <AboutCompany />
            </PermissionProtectedRoute>
          </SidebarLayout>
        } />
        <Route path="/organization/ai-settings" element={<SidebarLayout type="organization"><AISettings /></SidebarLayout>} />
        <Route path="/organization/billing" element={<SidebarLayout type="organization"><BillingSettings /></SidebarLayout>} />
        <Route path="/organization/settings" element={<SidebarLayout type="organization"><GeneralSettings /></SidebarLayout>} />
        <Route path="/organization/clients" element={
          <SidebarLayout type="organization">
            <PermissionProtectedRoute 
              anyPermissions={[PERMISSIONS.READ_CLIENTS, PERMISSIONS.MANAGE_CLIENTS]}
              deniedMessage="You need permission to view clients"
            >
              <Clients />
            </PermissionProtectedRoute>
          </SidebarLayout>
        } />
        <Route path="/organization/clients/:clientId" element={
          <SidebarLayout type="organization">
            <PermissionProtectedRoute 
              anyPermissions={[PERMISSIONS.READ_CLIENTS, PERMISSIONS.MANAGE_CLIENTS]}
              deniedMessage="You need permission to view client details"
            >
              <ClientDashboard />
            </PermissionProtectedRoute>
          </SidebarLayout>
        } />
        <Route path="/organization/clients/:clientId/events" element={
          <SidebarLayout type="organization">
            <PermissionProtectedRoute 
              anyPermissions={[PERMISSIONS.READ_CLIENTS, PERMISSIONS.MANAGE_CLIENTS]}
              deniedMessage="You need permission to view client events"
            >
              <ClientEvents />
            </PermissionProtectedRoute>
          </SidebarLayout>
        } />
        <Route path="/organization/events/:eventId" element={
          <SidebarLayout type="organization">
            <PermissionProtectedRoute 
              anyPermissions={[PERMISSIONS.READ_EVENTS, PERMISSIONS.MANAGE_EVENTS]}
              deniedMessage="You need permission to view events"
            >
              <EventDashboard />
            </PermissionProtectedRoute>
          </SidebarLayout>
        } />
        <Route path="/organization/events/:eventId/attendees" element={
          <SidebarLayout type="organization">
            <PermissionProtectedRoute 
              anyPermissions={[PERMISSIONS.READ_ATTENDEES, PERMISSIONS.MANAGE_ATTENDEES]}
              deniedMessage="You need permission to view attendees"
            >
              <EventAttendees />
            </PermissionProtectedRoute>
          </SidebarLayout>
        } />
        <Route path="/organization/venues" element={
          <SidebarLayout type="organization">
            <PermissionProtectedRoute 
              anyPermissions={[PERMISSIONS.READ_VENUES, PERMISSIONS.MANAGE_VENUES]}
              deniedMessage="You need permission to view venues"
            >
              <VenuesManagement />
            </PermissionProtectedRoute>
          </SidebarLayout>
        } />
        <Route path="/organization/venues/ai-research" element={
          <SidebarLayout type="organization">
            <PermissionProtectedRoute 
              anyPermissions={[PERMISSIONS.CREATE_VENUES, PERMISSIONS.MANAGE_VENUES]}
              deniedMessage="You need permission to use AI venue research"
            >
              <VenueResearch />
            </PermissionProtectedRoute>
          </SidebarLayout>
        } />
        <Route path="/organization/documentation" element={<SidebarLayout type="organization"><Documentation /></SidebarLayout>} />
        <Route path="/organization/destinations" element={
          <SidebarLayout type="organization">
            <PermissionProtectedRoute
              anyPermissions={[PERMISSIONS.MANAGE_DESTINATIONS, PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS]}
              deniedMessage="You need permission to manage destination library"
            >
              <DestinationLibrary />
            </PermissionProtectedRoute>
          </SidebarLayout>
        } />
        <Route path="/organization/event-library" element={
          <SidebarLayout type="organization">
            <PermissionProtectedRoute
              anyPermissions={[PERMISSIONS.MANAGE_DESTINATIONS, PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS]}
              deniedMessage="You need permission to manage the event library"
            >
              <EventLibrary />
            </PermissionProtectedRoute>
          </SidebarLayout>
        } />
        <Route path="/organization/activity-library" element={
          <SidebarLayout type="organization">
            <PermissionProtectedRoute
              anyPermissions={[PERMISSIONS.MANAGE_DESTINATIONS, PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS]}
              deniedMessage="You need permission to manage the activity library"
            >
              <ActivityLibrary />
            </PermissionProtectedRoute>
          </SidebarLayout>
        } />
        <Route path="/onboarding" element={<Onboarding />} />
        {/* Content plan routes removed in Milestone 7 */}
        {/* Background generator route removed in Milestone 3 */}
        <Route path="/settings/account" element={<SidebarLayout type="account"><ProfilePage /></SidebarLayout>} />
        <Route path="/settings/account/profile" element={<SidebarLayout type="account"><ProfilePage /></SidebarLayout>} />
        <Route path="/settings/account/security" element={<SidebarLayout type="account"><SecurityPage /></SidebarLayout>} />
        <Route path="/settings/account/sessions" element={<SidebarLayout type="account"><SessionsPage /></SidebarLayout>} />
        <Route path="/settings/account/danger" element={<SidebarLayout type="account"><DangerPage /></SidebarLayout>} />
        <Route path="/settings/members" element={<AdminPageWithHeader><MembersManager /></AdminPageWithHeader>} />
        {/* /accept-invite is now handled in public router (main.tsx) */}
        <Route path="*" element={<DashboardWithHeader />} />
      </Routes>
    </>
  );
}

function DashboardWithHeader() {
  return (
    <div style={{ backgroundColor: '#efede7', minHeight: '100vh' }}>
      <div className="h-16 bg-white border-b flex items-center justify-between px-6">
        <Link to="/dashboard" className="flex items-center hover:opacity-80 transition-opacity">
          <img 
            src="/images/logo_moniker_cockpit.png" 
            alt="Moniker Cockpit" 
            className="h-6 object-contain"
          />
        </Link>
        <UserMenu />
      </div>
      <DashboardPage />
    </div>
  );
}

function AdminPageWithHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#efede7' }}>
      <div className="h-16 bg-white border-b flex items-center justify-between px-6 flex-shrink-0">
        <Link to="/dashboard" className="flex items-center hover:opacity-80 transition-opacity">
          <img 
            src="/images/logo_moniker_cockpit.png" 
            alt="Moniker Cockpit" 
            className="h-6 object-contain"
          />
        </Link>
        <UserMenu />
      </div>
      <div className="flex-1 overflow-auto">
        {children}
      </div>
    </div>
  );
}
